import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Award, BookOpen, Target, CheckCircle } from 'lucide-react';

const BSMSCoursePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/courses"
            className="inline-flex items-center text-blue-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Courses
          </Link>
          <h1 className="text-4xl font-bold mb-4">BSMS - Bachelor of Siddha Medicine & Surgery</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            A comprehensive 5.5-year program in traditional Siddha medicine, combining ancient healing wisdom with modern medical practices.
          </p>
        </div>
      </div>

      {/* Course Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Overview</h2>
              <p className="text-gray-700 mb-4">
                Bachelor of Siddha Medicine & Surgery (BSMS) is a professional undergraduate degree program that focuses on the ancient Indian system of medicine known as Siddha. This comprehensive course integrates traditional Siddha healing practices with modern medical science, preparing students to become qualified Siddha medical practitioners.
              </p>
              <p className="text-gray-700">
                The program emphasizes the holistic approach to healthcare, incorporating herbal medicine, mineral-based treatments, yoga, meditation, and lifestyle modifications for disease prevention and cure.
              </p>
            </div>

            {/* Course Structure */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Structure</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Duration & Structure</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      5.5 years (including 1 year internship)
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      4.5 years academic + 1 year internship
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Semester-based curriculum
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Subjects</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Siddha Philosophy & Principles</li>
                    <li>• Herbal Pharmacology</li>
                    <li>• Toxicology & Alchemy</li>
                    <li>• Clinical Medicine</li>
                    <li>• Yoga & Meditation</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Career Opportunities */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Career Opportunities</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Clinical Practice</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Siddha Medical Practitioner</li>
                    <li>• Private Clinic Owner</li>
                    <li>• Hospital Consultant</li>
                    <li>• Wellness Center Director</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Other Opportunities</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Research Scientist</li>
                    <li>• Pharmaceutical Industry</li>
                    <li>• Academic Positions</li>
                    <li>• Health Consultant</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility Criteria</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Academic Requirements</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 10+2 with Physics, Chemistry, Biology</li>
                    <li>• Minimum 50% aggregate marks</li>
                    <li>• Age: 17-25 years</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Entrance Exams</h3>
                  <p className="text-gray-700">NEET (National Eligibility cum Entrance Test) qualification required</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Facts */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Facts</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-blue-600 mr-3" />
                  <span className="text-gray-700">Duration: 5.5 Years</span>
                </div>
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-3" />
                  <span className="text-gray-700">Mode: Full-time</span>
                </div>
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-blue-600 mr-3" />
                  <span className="text-gray-700">Degree: BSMS</span>
                </div>
                <div className="flex items-center">
                  <BookOpen className="h-5 w-5 text-blue-600 mr-3" />
                  <span className="text-gray-700">Recognition: UGC Approved</span>
                </div>
              </div>
            </div>

            {/* Top Colleges */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Top BSMS Colleges</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Government Siddha Medical College, Chennai</h4>
                  <p className="text-sm text-gray-600">Tamil Nadu</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Siddha Central Research Institute, Chennai</h4>
                  <p className="text-sm text-gray-600">Tamil Nadu</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900">National Institute of Siddha, Chennai</h4>
                  <p className="text-sm text-gray-600">Tamil Nadu</p>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white">
              <h3 className="text-xl font-bold mb-3">Need Guidance?</h3>
              <p className="mb-4">Get expert counseling for BSMS admissions</p>
              <Link
                to="/premium-services"
                className="inline-block bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Get Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BSMSCoursePage;